<template>
  <div class="resource-monitor-step" :class="{ 'is-disabled': isDisabled }">
    <div v-if="isDisabled" class="disabled-overlay">
      <p>{{ $t('forwarders.moduleNotSelected') }}</p>
    </div>

    <div class="form-section">
      <!-- Check Interval -->
      <div class="field">
        <label class="label">
          {{ $t('forwarders.checkInterval') }}
          <span class="required-mark">*</span>
          <span
            class="info-icon clickable"
            @click="showCheckIntervalTooltip = !showCheckIntervalTooltip"
          >(!)</span>
          <div v-if="showCheckIntervalTooltip" class="tooltip-content">
            <p><strong>{{ $t('forwarders.checkIntervalRules') }}:</strong></p>
            <ul>
              <li>{{ $t('forwarders.checkIntervalRuleMin') }}</li>
              <li>{{ $t('forwarders.checkIntervalRuleMax') }}</li>
            </ul>
          </div>
        </label>
        <div class="control">
          <input
            v-model.number="localValue.check_interval_seconds"
            class="input"
            :class="{ 'is-danger': checkIntervalError }"
            type="number"
            min="3"
            max="86400"
            :placeholder="$t('forwarders.enterCheckInterval')"
            @input="validateCheckInterval"
            @blur="handleCheckIntervalBlur"
          >
        </div>
        <p v-if="!checkIntervalError" class="help">{{ $t('forwarders.checkIntervalUnit') }} (3 - 86400)</p>
        <p v-if="checkIntervalError" class="help is-danger">{{ checkIntervalError }}</p>
      </div>

      <!-- Send Configuration -->
      <div class="config-group">
        <h4 class="group-title">{{ $t('forwarders.sendLogConfig') }}</h4>

        <div class="field-row">
          <div class="field">
            <label class="label">
              {{ $t('forwarders.timeInterval') }}
              <span class="required-mark">*</span>
              <span
                class="info-icon clickable"
                @click="showIntervalTooltip = !showIntervalTooltip"
              >(!)</span>
              <div v-if="showIntervalTooltip" class="tooltip-content">
                <p><strong>{{ $t('forwarders.timeIntervalRules') }}:</strong></p>
                <ul>
                  <li>{{ $t('forwarders.timeIntervalRuleMin') }}</li>
                  <li>{{ $t('forwarders.timeIntervalRuleMax') }}</li>
                </ul>
              </div>
            </label>
            <div class="control">
              <input
                v-model.number="localValue.log_send_interval_seconds"
                class="input"
                :class="{ 'is-danger': intervalError }"
                type="number"
                min="3"
                max="86400"
                :placeholder="$t('forwarders.enterTimeInterval')"
                @input="validateInterval"
                @blur="handleIntervalBlur"
              >
            </div>
            <p v-if="!intervalError" class="help">{{ $t('forwarders.timeIntervalUnit') }} (3 - 86400)</p>
            <p v-if="intervalError" class="help is-danger">{{ intervalError }}</p>
          </div>

          <div class="field">
            <label class="label">
              {{ $t('forwarders.logCount') }}
              <span class="required-mark">*</span>
              <span
                class="info-icon clickable"
                @click="showLogCountTooltip = !showLogCountTooltip"
              >(!)</span>
              <div v-if="showLogCountTooltip" class="tooltip-content">
                <p><strong>{{ $t('forwarders.logCountRules') }}:</strong></p>
                <ul>
                  <li>{{ $t('forwarders.logCountRuleMin') }}</li>
                  <li>{{ $t('forwarders.logCountRuleMax') }}</li>
                </ul>
              </div>
            </label>
            <div class="control">
              <input
                v-model.number="localValue.batch_size"
                class="input"
                :class="{ 'is-danger': batchSizeError }"
                type="number"
                min="1"
                max="999999"
                :placeholder="$t('forwarders.enterLogCount')"
                @input="validateBatchSize"
                @blur="handleBatchSizeBlur"
              >
            </div>
            <p v-if="!batchSizeError" class="help">(1 - 999999)</p>
            <p v-if="batchSizeError" class="help is-danger">{{ batchSizeError }}</p>
          </div>
        </div>
      </div>

      <!-- Index -->
      <div class="field">
        <label class="label">
          {{ $t('index') }}
          <span class="required-mark">*</span>
        </label>
        <div class="control">
          <v-select
            v-model="localValue.index_id"
            :options="indexOptions"
            :reduce="option => option.id"
            label="name"
            :placeholder="$t('forwarders.selectIndex')"
            :filterable="true"
            :clearable="false"
          >
            <template #no-options>
              {{ $t('forwarders.noIndexAvailable') }}
            </template>
          </v-select>
        </div>
      </div>

      <!-- Source Type -->
      <div class="field">
        <label class="label">
          {{ $t('forwarders.sourceType') }}
          <span class="required-mark">*</span>
        </label>
        <div class="control">
          <input
            :value="getSourceTypeName(localValue.source_type_id)"
            class="input"
            type="text"
            disabled
            readonly
          >
        </div>
      </div>

      <!-- Metrics Selection -->
      <div class="field">
        <label class="label">
          {{ $t('forwarders.metrics') }}
        </label>
        <div class="metrics-selection">
          <div
            v-for="metric in availableMetrics"
            :key="metric.value"
            class="metric-item"
          >
            <input
              :id="`metric-${metric.value}`"
              v-model="localValue.metrics"
              type="checkbox"
              :value="metric.value"
              class="checkbox"
            >
            <label :for="`metric-${metric.value}`" class="metric-label">
              {{ metric.label }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'ResourceMonitorStep',
  components: {
    vSelect
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    indexNames: {
      type: [Object, Array],
      default: () => ({})
    },
    resourceMonitorSourceTypes: {
      type: Array,
      default: () => []
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localValue: { ...this.value },
      checkIntervalError: '',
      intervalError: '',
      batchSizeError: '',
      showCheckIntervalTooltip: false,
      showIntervalTooltip: false,
      showLogCountTooltip: false,
      availableMetrics: [
        { value: 'cpu', label: this.$t('forwarders.metricCPU') },
        { value: 'ram', label: this.$t('forwarders.metricRAM') },
        { value: 'disk', label: this.$t('forwarders.metricDisk') },
        { value: 'network', label: this.$t('forwarders.metricNetwork') }
      ]
    }
  },
  computed: {
    indexOptions() {
      return Array.isArray(this.indexNames)
        ? this.indexNames
        : Object.values(this.indexNames || {})
    }
  },
  watch: {
    value: {
      handler(newVal) {
        // Avoid deep clone on every change - only update if really needed
        if (JSON.stringify(this.localValue) !== JSON.stringify(newVal)) {
          this.localValue = JSON.parse(JSON.stringify(newVal))
        }
      },
      deep: true
    },
    localValue: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  mounted() {
    // Set default source type
    if (this.resourceMonitorSourceTypes.length > 0 && !this.localValue.source_type_id) {
      this.localValue.source_type_id = this.resourceMonitorSourceTypes[0].id
    }
    // Validate initial values
    this.validateCheckInterval()
    this.validateInterval()
    this.validateBatchSize()
    // Add click outside listener for tooltips
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(event) {
      const tooltipTriggers = this.$el.querySelectorAll('.info-icon.clickable')
      const tooltipContents = this.$el.querySelectorAll('.tooltip-content')

      let clickedInsideTooltip = false

      tooltipTriggers.forEach(trigger => {
        if (trigger.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })

      tooltipContents.forEach(content => {
        if (content.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })

      if (!clickedInsideTooltip) {
        this.showCheckIntervalTooltip = false
        this.showIntervalTooltip = false
        this.showLogCountTooltip = false
      }
    },
    validateCheckInterval() {
      const value = this.localValue.check_interval_seconds

      if (value < 3) {
        this.checkIntervalError = this.$t('forwarders.checkIntervalRangeError')
        return false
      }

      if (value > 86400) {
        this.checkIntervalError = this.$t('forwarders.checkIntervalRangeError')
        return false
      }

      this.checkIntervalError = ''
      return true
    },
    handleCheckIntervalBlur() {
      const value = this.localValue.check_interval_seconds
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.check_interval_seconds = 60
      } else if (value < 3) {
        // Set to minimum value if less than 3
        this.localValue.check_interval_seconds = 3
      } else if (value > 86400) {
        // Set to maximum value if greater than 86400
        this.localValue.check_interval_seconds = 86400
      }
      this.checkIntervalError = ''
    },
    validateInterval() {
      const value = this.localValue.log_send_interval_seconds

      if (value < 3) {
        this.intervalError = this.$t('forwarders.intervalRangeError')
        return false
      }

      if (value > 86400) {
        this.intervalError = this.$t('forwarders.intervalRangeError')
        return false
      }

      this.intervalError = ''
      return true
    },
    handleIntervalBlur() {
      const value = this.localValue.log_send_interval_seconds
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.log_send_interval_seconds = 600
      } else if (value < 3) {
        // Set to minimum value if less than 3
        this.localValue.log_send_interval_seconds = 3
      } else if (value > 86400) {
        // Set to maximum value if greater than 86400
        this.localValue.log_send_interval_seconds = 86400
      }
      this.intervalError = ''
    },
    validateBatchSize() {
      const value = this.localValue.batch_size

      if (value < 1) {
        this.batchSizeError = this.$t('forwarders.batchSizeRangeError')
        return false
      }

      if (value > 999999) {
        this.batchSizeError = this.$t('forwarders.batchSizeRangeError')
        return false
      }

      this.batchSizeError = ''
      return true
    },
    handleBatchSizeBlur() {
      const value = this.localValue.batch_size
      // Reset to default value if empty
      if (!value && value !== 0) {
        this.localValue.batch_size = 500
      } else if (value < 1) {
        // Set to minimum value if less than 1
        this.localValue.batch_size = 1
      } else if (value > 999999) {
        // Set to maximum value if greater than 999999
        this.localValue.batch_size = 999999
      }
      this.batchSizeError = ''
    },
    getSourceTypeName(sourceTypeId) {
      const sourceType = this.resourceMonitorSourceTypes.find(st => st.id === sourceTypeId)
      return sourceType ? sourceType.name : ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/dark-modal.scss';

.resource-monitor-step {
  position: relative;

  &.is-disabled {
    opacity: 0.5;
    pointer-events: none;

    .disabled-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgba(26, 29, 46, 0.95);
      padding: 2rem;
      border-radius: 8px;
      text-align: center;
      z-index: 10;
      pointer-events: auto;

      p {
        color: #fff;
        font-size: 1rem;
        margin: 0;
      }
    }
  }

  .form-section {
    max-width: 700px;
  }

  .config-group {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 4px;

    .group-title {
      color: #fff;
      font-size: 0.95rem;
      font-weight: 600;
      margin-bottom: 1rem;
    }
  }

  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .field {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label {
    color: $modal-text;
    font-weight: 500;
    margin-bottom: 0.35rem;
    display: block;
    position: relative;

    .required-mark {
      color: $modal-required;
      margin-left: 2px;
    }

    .info-icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      line-height: 16px;
      text-align: center;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
      color: #fff;
      font-size: 0.75rem;
      margin-left: 4px;
      font-style: normal;
      transition: all 0.2s;

      &.clickable {
        cursor: pointer;

        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }
      }
    }

    .tooltip-content {
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 0.5rem;
      padding: 1rem;
      background-color: #2a2d36;
      border: 1px solid #404553;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 1000;
      min-width: 300px;
      max-width: 400px;

      p {
        color: #fff;
        margin: 0 0 0.5rem 0;
        font-size: 0.9rem;

        strong {
          color: $modal-primary;
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: rgba(255, 255, 255, 0.85);
          font-size: 0.85rem;
          line-height: 1.6;
          padding-left: 1rem;
          position: relative;
          margin-bottom: 0.25rem;

          &:before {
            content: "•";
            position: absolute;
            left: 0;
            color: $modal-primary;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .control {
    .input {
      background-color: $modal-input-bg;
      border: 1px solid $modal-border;
      color: $modal-text;
      border-radius: 4px;
      padding: 0.75rem;
      width: 100%;
      font-size: 0.95rem;

      &:focus {
        border-color: $modal-primary;
        outline: none;
      }

      &:disabled,
      &[readonly] {
        background-color: $modal-bg;
        cursor: not-allowed;
        opacity: 0.7;
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }

      &.is-danger {
        border-color: $modal-required;

        &:focus {
          border-color: $modal-required;
          box-shadow: 0 0 0 0.125em rgba(255, 71, 87, 0.25);
        }
      }
    }
  }

  .help {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    margin-top: 0.25rem;

    &.is-danger {
      color: $modal-required;
    }
  }

  .metrics-selection {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    padding: 1rem;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 4px;

    .metric-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .checkbox {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: $modal-primary;
      }

      .metric-label {
        color: $modal-text;
        font-size: 0.95rem;
        cursor: pointer;
        user-select: none;
        margin: 0;
      }
    }
  }
}
</style>
